/**
 * Display ISO datetime strings exactly as stored — no timezone math.
 *
 * Customers enter the pickup time as local time at the pickup location
 * (e.g. "01:20" in Hurghada). We store and display that string verbatim,
 * so every party (customer, driver, admin) sees the same time regardless
 * of their browser timezone or DST rules.
 *
 * Accepts strings like:
 *   "2026-09-26T01:20:00"
 *   "2026-09-26T01:20:00.000Z"        (legacy: stored before the migration)
 *   "2026-09-26T01:20:00+00:00"
 *   Date objects
 *
 * Returns "26.09.2026 01:20" — the exact wall-clock time encoded in the string.
 */
export function formatPickup(value, opts = {}) {
  if (!value) return '';

  const showSeconds = !!opts.seconds;
  const dateOnly = !!opts.dateOnly;
  const timeOnly = !!opts.timeOnly;

  // Accept Date objects too (used for sorting elsewhere)
  let s = value;
  if (value instanceof Date) {
    s = value.toISOString();
  }
  s = String(s);

  // Strip trailing Z or +HH:MM offset — we want the wall clock as written
  s = s.replace(/Z$/, '').replace(/[+-]\d{2}:?\d{2}$/, '');

  // Expect "YYYY-MM-DDTHH:MM[:SS[.fff]]"
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return String(value);
  const [, y, mo, d, hh, mm, ss] = m;

  const datePart = `${d}.${mo}.${y}`;
  if (dateOnly) return datePart;

  const timePart = showSeconds && ss
    ? `${hh || '00'}:${mm || '00'}:${ss}`
    : `${hh || '00'}:${mm || '00'}`;

  if (timeOnly) return timePart;
  return `${datePart} ${timePart}`;
}

export function formatPickupDate(value) {
  return formatPickup(value, { dateOnly: true });
}

export function formatPickupTime(value) {
  return formatPickup(value, { timeOnly: true });
}
